import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DownArrowIcon } from '../../../images/svg/CustomizedSVG';
import { commonTernary } from '../../../services/commercialService';
import { CustomSelectStyles as classes } from '../../../components/LeadGeneration/CustomSelect/CustomSelectStyles';
import LeadGenerationContext from '../../../context/LeadGenerationContext';

const CatalogueEntryDetails = (props) => {
  const { t } = useTranslation();
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { history } = props;
  const { theme } = leadGenerationContext;
  const [entryProductType, setEntryProductType] = useState('');
  const [entryProductName, setEntryProductName] = useState('');
  const [language, setLanguage] = useState('');
  const productTypes = ['Checking Account', 'Savings Account', 'Certificate of Deposit', 'IRA'];
  const productsEN = [
    'Libre Account',
    'Elite Account',
    'Ahorra Account',
    'ABC Account',
    'CD IRA',
    'Educational IRA',
    'Certificate of Deposit',
  ];
  const productsES = [
    'Cuenta Libre',
    'Cuenta Elite',
    'Cuenta Ahorra',
    'Cuenta ABC',
    'CD IRA',
    'IRA Educativa',
    'Certificado de Depósito',
  ];
  const products = language === 'ES' ? productsES : productsEN;

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('hide-header', {
        bubbles: true,
        composed: true,
      })
    );
  }, []);

  useEffect(() => {
    setEntryProductName('');
  }, [language]);

  const commonSelectFunction = (data1, data2) => {
    let data;
    if (data1) {
      data = data1;
    } else {
      data = data2;
    }
    return data;
  };

  const handleProductTypeChange = (event) => {
    setEntryProductType(event.target.value);
  };

  const handleProductNameChange = (event) => {
    setEntryProductName(event.target.value);
  };

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  const handleContinue = () => {
    if (!language) return;

    history.push('/catalogue-wc', {
      entryProductType,
      entryProductName,
      language,
    });
  };

  return (
    <Grid
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: 2,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Grid item size={{ lg: 6, md: 6, sm: 6, xs: 6 }} sx={{ fontSize: '20px' }}>
        Catalogue Entry Details
      </Grid>

      <Grid item sx={{ width: '50%', '@media (max-width:600px)': { width: '75%' } }}>
        <InputLabel htmlFor="language-select" data-testid="language-select-testid">
          Select Language
        </InputLabel>
        <Grid sx={{ position: 'relative' }}>
          <FormControl fullWidth>
            <Select
              aria-label={commonSelectFunction('language', null)}
              id={commonSelectFunction('language-select', null)}
              role="listbox"
              inputProps={{ 'data-testid': commonSelectFunction('language-select-testid', null) }}
              fullWidth
              value={commonSelectFunction(language || '', '')}
              onChange={handleLanguageChange}
              renderValue={(val) => (val ? String(val) : t('Select', { defaultValue: 'Select' }))}
              displayEmpty
              // eslint-disable-next-line react/no-unstable-nested-components
              IconComponent={() => (
                <Box padding="0 10px 0 0">
                  <DownArrowIcon
                    style={{ width: 24, height: 24 }}
                    color={theme?.palette?.orientalPrimary?.main}
                  />
                </Box>
              )}
              sx={[
                classes.select_inputs,
                [
                  {
                    '&.Mui-focused': {
                      border: `1px solid ${theme?.palette?.primary?.main}`,
                    },
                  },
                ],
              ]}
              MenuProps={{
                disablePortal: true,
                container: document.body,
                PaperProps: {
                  sx: {
                    position: 'absolute',
                    mt: 1,
                    zIndex: 1300,
                    ...theme?.menuBackground,
                    border: `1px solid ${theme?.menuBackground?.borderColor} !important`,
                    color: theme?.palette?.primary?.main,
                  },
                },
              }}
              slotProps={{
                icon: {
                  sx: classes.select_icon,
                },
                select: {
                  sx: [
                    commonTernary(
                      language === null || language === '' || language === 'Select',
                      classes.select_style
                    ),
                  ],
                },
              }}
            >
              <MenuItem role="option" value="">
                {t('Select', { defaultValue: 'Select' })}
              </MenuItem>
              <MenuItem role="option" value="EN">
                {t('EN', { defaultValue: 'EN' })}
              </MenuItem>
              <MenuItem role="option" value="ES">
                {t('ES', { defaultValue: 'ES' })}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid item sx={{ width: '50%', '@media (max-width:600px)': { width: '75%' } }}>
        <InputLabel htmlFor="product-type-select" data-testid="product-type-select-testid">
          Select Product Type
        </InputLabel>
        <Grid sx={{ position: 'relative' }}>
          <FormControl fullWidth>
            <Select
              aria-label={commonSelectFunction('product type', null)}
              id={commonSelectFunction('product-type-select', null)}
              role="listbox"
              inputProps={{
                'data-testid': commonSelectFunction('product-type-select-testid', null),
              }}
              fullWidth
              value={commonSelectFunction(entryProductType || '', '')}
              onChange={handleProductTypeChange}
              renderValue={(val) => (val ? String(val) : t('Select', { defaultValue: 'Select' }))}
              displayEmpty
              // eslint-disable-next-line react/no-unstable-nested-components
              IconComponent={() => (
                <Box padding="0 10px 0 0">
                  <DownArrowIcon
                    style={{ width: 24, height: 24 }}
                    color={theme?.palette?.orientalPrimary?.main}
                  />
                </Box>
              )}
              sx={[
                classes.select_inputs,
                [
                  {
                    '&.Mui-focused': {
                      border: `1px solid ${theme?.palette?.primary?.main}`,
                    },
                  },
                ],
              ]}
              MenuProps={{
                disablePortal: true,
                container: document.body,
                PaperProps: {
                  sx: {
                    position: 'absolute',
                    mt: 1,
                    zIndex: 1300,
                    ...theme?.menuBackground,
                    border: `1px solid ${theme?.menuBackground?.borderColor} !important`,
                    color: theme?.palette?.primary?.main,
                  },
                },
              }}
              slotProps={{
                icon: {
                  sx: classes.select_icon,
                },
                select: {
                  sx: [
                    commonTernary(
                      entryProductType === null ||
                        entryProductType === '' ||
                        entryProductType === 'Select',
                      classes.select_style
                    ),
                  ],
                },
              }}
            >
              <MenuItem role="option" value="">
                {t('Select', { defaultValue: 'Select' })}
              </MenuItem>
              {productTypes.length > 0 &&
                productTypes.map((option) => (
                  <MenuItem key={option} role="option" value={option}>
                    {option}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid item sx={{ width: '50%', '@media (max-width:600px)': { width: '75%' } }}>
        <InputLabel htmlFor="product-name-select" data-testid="product-name-select-testid">
          Select Product Name
        </InputLabel>
        <Grid sx={{ position: 'relative' }}>
          <FormControl fullWidth>
            <Select
              aria-label={commonSelectFunction('product name', null)}
              id={commonSelectFunction('product-name-select', null)}
              role="listbox"
              inputProps={{
                'data-testid': commonSelectFunction('product-name-select-testid', null),
              }}
              fullWidth
              value={commonSelectFunction(entryProductName, '')}
              onChange={handleProductNameChange}
              renderValue={(val) => (val ? String(val) : t('Select', { defaultValue: 'Select' }))}
              displayEmpty
              // eslint-disable-next-line react/no-unstable-nested-components
              IconComponent={() => (
                <Box padding="0 10px 0 0">
                  <DownArrowIcon
                    style={{ width: 24, height: 24 }}
                    color={theme?.palette?.orientalPrimary?.main}
                  />
                </Box>
              )}
              sx={[
                classes.select_inputs,
                [
                  {
                    '&.Mui-focused': {
                      border: `1px solid ${theme?.palette?.primary?.main}`,
                    },
                  },
                ],
              ]}
              MenuProps={{
                disablePortal: true,
                container: document.body,
                PaperProps: {
                  sx: {
                    position: 'absolute',
                    mt: 1,
                    zIndex: 1300,
                    ...theme?.menuBackground,
                    border: `1px solid ${theme?.menuBackground?.borderColor} !important`,
                    color: theme?.palette?.primary?.main,
                  },
                },
              }}
              slotProps={{
                icon: {
                  sx: classes.select_icon,
                },
                select: {
                  sx: [
                    commonTernary(
                      entryProductName === null ||
                        entryProductName === '' ||
                        entryProductName === 'Select',
                      classes.select_style
                    ),
                  ],
                },
              }}
            >
              <MenuItem role="option" value="">
                {t('Select', { defaultValue: 'Select' })}
              </MenuItem>
              {products.length > 0 &&
                products.map((option) => (
                  <MenuItem key={option} role="option" value={option}>
                    {option}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid item>
        <Button
          variant="contained"
          color="primary"
          display="flex"
          justifyContent="right"
          onClick={handleContinue}
        >
          Continue
        </Button>
      </Grid>
    </Grid>
  );
};

export default withRouter(CatalogueEntryDetails);

CatalogueEntryDetails.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
